/**
 * Preço promocional — função PURA (sem banco, sem DOM), issue #204/#210.
 *
 * Fonte única da regra "esta promoção vale agora e quanto fica o preço?".
 * MOVIDA de shared/src/utils/promotion-pricing.ts para o pacote
 * `client-core` (#210): as regras de EXIBIÇÃO de preço/selo/faixa do
 * cardápio vivem aqui, usadas pelo menu web e pelo futuro app. A API usa
 * para cobrar (quote/orders, sempre no servidor) — a cobrança continua
 * recalculada no servidor; o cliente só EXIBE com estas funções.
 *
 * Regras (spec do App cliente §8.2–§8.3, decisão #2 do Eduardo):
 *  - uma promoção é de UM restaurante; quem chama passa só as do tenant;
 *  - vigência: `starts_at <= agora < ends_at` (Unix seconds, ambos opcionais),
 *    dia da semana (bitmask, bit 0 = segunda) e janela `HH:MM` no fuso do
 *    restaurante; janela que vira a noite (22:00–02:00) vale no dia em que
 *    começa (a madrugada seguinte conta como o dia anterior);
 *  - `percent` incide só sobre o preço do item (extras ficam fora — D-P2);
 *  - `fixed_price` é o PREÇO FINAL e só vale se for < preço de lista (preço
 *    base mudou depois? promoção deixa de ter efeito, sem erro);
 *  - arredondamento: `Math.round` sobre centavos, como o resto do cálculo.
 */
import { nowInRestaurant } from '@self-restaurant/shared'

export type PromotionRuleLike = {
  id: string
  discount_type: 'percent' | 'fixed_price'
  discount_value: number
  /** nulo = todas as variantes do produto */
  variant_id: string | null
  starts_at: number | null
  ends_at: number | null
  /** bitmask: bit 0 = segunda … bit 6 = domingo; nulo = todos os dias */
  days_of_week: number | null
  /** 'HH:MM' no fuso do restaurante; ambos ou nenhum */
  start_time: string | null
  end_time: string | null
  is_active: boolean
  /** rótulo opcional da promoção (só para exibição — não afeta o preço) */
  label?: string | null
}

export type PromotionPrice = {
  /** preço de lista (sem promoção) por unidade, em centavos */
  listUnitCents: number
  /** preço final por unidade (== lista quando não há promoção válida) */
  unitCents: number
  /** promoção aplicada, ou null */
  promotionId: string | null
}

const toMinutes = (hhmm: string): number => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5))

/** Bit do bitmask (bit 0 = segunda) para o dia da semana 0=domingo…6=sábado. */
const dayBit = (dayOfWeek: number): number => 1 << ((dayOfWeek + 6) % 7)

/** A promoção está vigente em `nowSeconds` no fuso do restaurante? */
export function isPromotionActiveAt(
  rule: PromotionRuleLike,
  nowSeconds: number,
  /** `null` = fuso do restaurante inválido/irresolvível */
  timezone: string | null,
): boolean {
  if (!rule.is_active) return false
  if (rule.starts_at !== null && nowSeconds < rule.starts_at) return false
  if (rule.ends_at !== null && nowSeconds >= rule.ends_at) return false

  const needsLocalClock = rule.days_of_week !== null || rule.start_time !== null
  if (!needsLocalClock) return true

  // Sem fuso válido não dá para saber "que horas são no restaurante": a
  // promoção com janela fica INATIVA (preço cheio) — nunca lança (#252).
  if (timezone === null) return false
  let local: ReturnType<typeof nowInRestaurant>
  try {
    local = nowInRestaurant(timezone, nowSeconds * 1000)
  } catch {
    return false
  }
  let dow = local.dayOfWeek

  if (rule.start_time !== null && rule.end_time !== null) {
    const start = toMinutes(rule.start_time)
    const end = toMinutes(rule.end_time)
    const m = local.minutes
    if (start < end) {
      if (m < start || m >= end) return false
    } else {
      // vira a noite: [start, 24h) no dia que começa, [0, end) na madrugada
      // seguinte, que pertence ao dia anterior
      if (m >= start) {
        // dia atual
      } else if (m < end) {
        dow = (dow + 6) % 7
      } else {
        return false
      }
    }
  }

  if (rule.days_of_week !== null && (rule.days_of_week & dayBit(dow)) === 0) return false
  return true
}

/** Preço final por unidade de lista `listUnitCents` sob uma única promoção. */
export function applyPromotion(
  rule: Pick<PromotionRuleLike, 'discount_type' | 'discount_value'>,
  listUnitCents: number,
): number {
  if (rule.discount_type === 'percent') {
    const pct = Math.min(Math.max(rule.discount_value, 0), 100)
    return Math.round((listUnitCents * (100 - pct)) / 100)
  }
  // fixed_price: só vale se realmente baratear
  return rule.discount_value < listUnitCents ? rule.discount_value : listUnitCents
}

/**
 * Escolhe e aplica a promoção ao item. `rules` são as promoções do produto
 * (do tenant dono). Uma promoção de variante só vale para a variante
 * escolhida; `variant_id` nulo vale para qualquer uma. Se mais de uma valer
 * (não deveria: índice único de "uma ativa por produto"), vence o menor preço.
 */
export function priceWithPromotion(input: {
  rules: readonly PromotionRuleLike[]
  listUnitCents: number
  variantId: string | null
  nowSeconds: number
  timezone: string | null
}): PromotionPrice {
  const { rules, listUnitCents, variantId, nowSeconds, timezone } = input
  let best: PromotionPrice = { listUnitCents, unitCents: listUnitCents, promotionId: null }
  for (const rule of rules) {
    if (rule.variant_id !== null && rule.variant_id !== variantId) continue
    if (!isPromotionActiveAt(rule, nowSeconds, timezone)) continue
    const unitCents = applyPromotion(rule, listUnitCents)
    if (unitCents < best.unitCents) best = { listUnitCents, unitCents, promotionId: rule.id }
  }
  return best
}

/**
 * Objeto `promotion` do payload `/menu` (issue #206): presente só quando a
 * promoção está vigente AGORA no fuso do restaurante. O cardápio usa para o
 * chip "−20%" e o "até 15h" (spec §8.5); `price_cents` é o preço final do
 * nível (produto ou variante) a que a promoção se aplica — o mesmo valor de
 * `promo_price_cents`.
 */
export type MenuPromotion = {
  id: string
  type: 'percent' | 'fixed_price'
  /** percent: 1–90 (pontos); fixed_price: preço final em centavos */
  value: number
  /** preço final já calculado para o nível (produto/variante) deste campo */
  price_cents: number
  /** rótulo opcional definido pelo restaurante */
  label?: string
  /** presente só quando a promoção tem janela de datas/dias/horário */
  window?: {
    starts_at?: number
    ends_at?: number
    /** bitmask: bit 0 = segunda … bit 6 = domingo */
    days_of_week?: number
    /** 'HH:MM' no fuso do restaurante */
    start_time?: string
    end_time?: string
  }
}

/** Mapeia a regra vigente para o objeto `promotion` do payload `/menu`. */
export function menuPromotion(rule: PromotionRuleLike, priceCents: number): MenuPromotion {
  const promotion: MenuPromotion = {
    id: rule.id,
    type: rule.discount_type,
    value: rule.discount_value,
    price_cents: priceCents,
  }
  if (rule.label) promotion.label = rule.label
  const hasWindow =
    rule.starts_at !== null ||
    rule.ends_at !== null ||
    rule.days_of_week !== null ||
    rule.start_time !== null
  if (hasWindow) {
    const window: NonNullable<MenuPromotion['window']> = {}
    if (rule.starts_at !== null) window.starts_at = rule.starts_at
    if (rule.ends_at !== null) window.ends_at = rule.ends_at
    if (rule.days_of_week !== null) window.days_of_week = rule.days_of_week
    if (rule.start_time !== null) window.start_time = rule.start_time
    // start_time e end_time são ambos ou nenhum (schema), mas o tipo é amplo
    if (rule.end_time !== null) window.end_time = rule.end_time
    promotion.window = window
  }
  return promotion
}

/**
 * Preço unitário a EXIBIR no cardápio (detalhe do produto, carrinho) a partir
 * do que GET /menu/categories devolve: `promo_price_cents` (preço com a
 * promoção vigente no momento da carga, calculado no servidor) ou, sem
 * promoção, o preço de lista. Com variante, vale o preço da variante (#255).
 * Só exibição: a cobrança é sempre recalculada no servidor (quote/pedido).
 */
export function displayUnitPrice(
  product: { price_cents: number; promo_price_cents?: number | null },
  variant?: { price_cents: number; promo_price_cents?: number | null } | null,
): { listUnitCents: number; unitCents: number; onPromotion: boolean } {
  const source = variant ?? product
  const listUnitCents = source.price_cents
  const promo = source.promo_price_cents
  const unitCents = typeof promo === 'number' && promo < listUnitCents ? promo : listUnitCents
  return { listUnitCents, unitCents, onPromotion: unitCents < listUnitCents }
}

/**
 * Preço a EXIBIR no card da LISTA do cardápio. Se o produto não tem variantes,
 * equivale a `displayUnitPrice(product)`. Com variantes, se alguma (ou o
 * produto) tiver promoção vigente, mostra o melhor preço promocional (menor
 * `unitCents`) com o de lista riscado — assim a lista indica promo mesmo
 * quando ela só vale para uma variante (#255 / QA #269).
 */
export function displayListCardPrice(product: {
  price_cents: number
  promo_price_cents?: number | null
  variants?: readonly { price_cents: number; promo_price_cents?: number | null }[]
}): { listUnitCents: number; unitCents: number; onPromotion: boolean } {
  const candidates = [
    displayUnitPrice(product),
    ...(product.variants ?? []).map((v) => displayUnitPrice(product, v)),
  ]
  const onPromo = candidates.filter((c) => c.onPromotion)
  if (onPromo.length === 0) return displayUnitPrice(product)
  return onPromo.reduce((best, c) => (c.unitCents < best.unitCents ? c : best))
}

/**
 * «a partir de» no Menu público (#481, decisão A da #299 — PR-1):
 * preço de PARTIDA de um prato com variantes, para os pontos de lista e
 * para o detalhe antes da escolha. O render (menu web) e a chave
 * `menu.price_from` ficam no PR-2; aqui só a regra.
 *
 * - **Gatilho (`from === true`)**: 2+ variantes RECEBIDAS com
 *   `price_cents > 0` e preços de LISTA distintos (critério 1). O critério
 *   de spread é sobre preços de LISTA, independente de promoções. A API do
 *   menu só serializa variantes disponíveis (#479, `not_orderable`), pelo
 *   que as indisponíveis ficam de fora por construção — nem contam para o
 *   gatilho nem para o valor (critério 3).
 * - **`from === false`** (sem variantes, uma só com preço, ou todas ao
 *   mesmo preço de lista): devolve `displayListCardPrice(product)` TAL COMO
 *   HOJE — regressão zero, promoção incluída (critério 4).
 * - **`from === true`**: o melhor `displayUnitPrice(product, v)` entre as
 *   variantes com preço — mínimo por `unitCents`, desempate pelo menor
 *   `listUnitCents` (determinístico; empate total fica com a primeira do
 *   payload). Sem promoção vigente, `unitCents` equivale a
 *   `basePriceFromVariants(variants)` do `@self-restaurant/shared` (#479,
 *   critério 2: mínimo das disponíveis com preço — equivalência fixada por
 *   teste). Com promoção, `unitCents` é o **mínimo pagável** e
 *   `listUnitCents` o preço de lista da MESMA variante (critério 6 + UX
 *   §2): o riscado é sempre o dessa variante, nunca o base do produto nem
 *   o de outra.
 *
 * PURA: sem banco, sem DOM; só exibição — a cobrança segue recalculada no
 * servidor pela variante escolhida (critério 9).
 */
export function fromPriceDisplay(product: {
  price_cents: number
  promo_price_cents?: number | null
  variants?: readonly { price_cents: number; promo_price_cents?: number | null }[]
}): { from: boolean; listUnitCents: number; unitCents: number; onPromotion: boolean } {
  const priced = (product.variants ?? []).filter((v) => v.price_cents > 0)
  // gatilho: 2+ preços de LISTA distintos entre as variantes com preço
  if (new Set(priced.map((v) => v.price_cents)).size < 2) {
    return { from: false, ...displayListCardPrice(product) }
  }
  // `priced` tem >= 2 elementos aqui: o reduce sem valor inicial é seguro.
  const best = priced
    .map((v) => displayUnitPrice(product, v))
    .reduce((best, c) =>
      c.unitCents < best.unitCents ||
      (c.unitCents === best.unitCents && c.listUnitCents < best.listUnitCents)
        ? c
        : best,
    )
  return { from: true, ...best }
}
